import { z } from "zod";

const dateRegexp = "[0-9]{4}-[0-9]{2}-[0-9]{2}";
export const timeRegexp = "[0-9]{2}:[0-9]{2}";
export const DateZod = z.string().regex(new RegExp(`^${dateRegexp}$`));
export const TimeZod = z.string().regex(new RegExp(`^${timeRegexp}$`));
export const DateTimeZod = z
  .string()
  .regex(new RegExp(`^${dateRegexp} ${timeRegexp}$`));

export const FotoZod = z.object({
  i: z.number(),
  t: z.number(),
  y: z.number(),
  m: z.string(),
  d: z.string(),
  h: z.string(),
  g: z.number(),
  // TODO: esto solo va en fotoVendida
  cantidad: z.number().optional(),
  cl: z.string(),
  precio: z.coerce.number().optional(),
  producto: z.number().optional(),
  borrada: z.boolean().optional(),
  descuento: z.coerce.number().optional(),
  promo: z.string().optional(),
  edicion: z.string().nullable().optional(),
});
export type Foto = z.infer<typeof FotoZod>;

export const FacturaZod = z.object({
  id: z.number(),
  sitio_id: z.number(),
  pto_venta: z.number(),
  numero: z.number(),
  fecha: DateZod,
  monto: z.coerce.number(),
  cae: z.string(),
  cuit: z.string(),
});

export const PedidoZod = z.object({
  id: z.number(),
  pagado: z.boolean(),
  fecha: z.union([DateZod, DateTimeZod]).nullable(),
  fotos: z.array(FotoZod),
  descuento: z.coerce.number(),
  productos: z.array(
    z.object({
      producto: z.number(),
      cantidad: z.number(),
      precio: z.coerce.number(),
    })
  ),
  obs: z.string().optional().nullable(),
  codLocal: z.string().optional(),
});

export const AdminPedidoZod = z.intersection(
  PedidoZod,
  z.object({
    factura: FacturaZod.nullable(),
    cliente: z.object({
      id: z.number(),
      email: z.string(),
      nombre: z.string(),
    }),
    codLocal: z.string(),
    subtotal: z.coerce.number(),
    vendedor: z.number().nullable(),
    notificacion: z.boolean().optional(),
    ticket: z.boolean().optional(),
  })
);

export type Pedido = z.infer<typeof PedidoZod>;
export type AdminPedido = z.infer<typeof AdminPedidoZod>;

export const UsuarioZod = z.object({
  carrito: z.number().nullable(),
  compradas: z.record(z.string(), z.number()).nullable(),
  defpass: z.boolean(),
  email: z.string().nullable(),
  encargadas: z.record(z.string(), z.number()),
  id: z.number().nullable(),
  nombre: z.string().nullable(),
  pedidos: z.record(z.string(), PedidoZod),
});
export type Usuario = z.infer<typeof UsuarioZod>;

export const AppStatusZod = z.object({
  temporadas: z.array(
    z.object({ id: z.number(), minf: DateZod, maxf: DateZod })
  ),
  state: z.object({
    usuario: UsuarioZod,
  }),
});

export type AppStatus = z.infer<typeof AppStatusZod>;

export const AdminZod = z.object({
  id: z.number().nullable(),
  nombre: z.string().nullable(),
  permisos: z.array(z.string()).nullable(),
  cuentas: z
    .array(
      z.object({
        id: z.number(),
        nombre: z.string(),
        recibe_pagos_local: z.boolean(),
      })
    )
    .nullable(),
});
export type Admin = z.infer<typeof AdminZod>;

export const AdminStatusZod = z.object({
  admin: AdminZod,
  monedas: z.record(z.string(), z.coerce.number()),
  productos_extra: z.array(
    z.object({
      id: z.number(),
      descripcion: z.string(),
      precio: z.coerce.number(),
    })
  ),
});

export type AdminStatus = z.infer<typeof AdminStatusZod>;

export const ClientesHabitualesZod = z.record(
  z.string(),
  z.object({ C: z.number(), P: z.number(), fotos: z.coerce.number() })
);

export type ClientesHabituales = z.infer<typeof ClientesHabitualesZod>;

export const FotografoZod = z.object({
  nombre: z.string(),
  codigo: z.string(),
});

export const TemporadaZod = z.object({
  id: z.number(),
  nombre: z.string(),
  fotografos: z.array(z.tuple([z.number(), z.number()])),
  lugares: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      shape: z.string(),
      coords: z.string(),
    })
  ),
  productos: z.array(
    z.object({
      id: z.number(),
      descripcion: z.string(),
      auto: z.boolean().optional(),
    })
  ),
  prioridad: z.number(),
  promos: z
    .array(z.object({ id: z.number(), descripcion: z.string() }))
    .optional(),
});
export type Temporada = z.infer<typeof TemporadaZod>;

export const ConfigZod = z.object({
  codLocal: z.string(),
  formas_de_pago: z.record(
    z.string(),
    z.object({ nombre: z.string(), logo: z.string().nullable() })
  ),
  fotografos: z.record(z.string(), FotografoZod),
  moneda: z.string(),
  promos: z.array(z.object({ id: z.number(), descripcion: z.string() })),
  temporada: z.number(),
  temporadas: z.record(z.string(), TemporadaZod),
  web: z.boolean(),
});

export type Config = z.infer<typeof ConfigZod>;

export const SubidaZod = z.object({
  id: z.number(),
  fotografo_id: z.number(),
  color: z.string(),
  fecha: z.string(),
  total: z.number(),
  subidas: z.number(),
  estado: z.string(),
});
export type Subida = z.infer<typeof SubidaZod>;

export const ComisionesFZod = z.object({
  temporada: z.object({
    id: z.number(),
    nombre: z.string(),
  }),
  fotografo: z.object({
    id: z.number(),
    nombre: z.string(),
    apellido: z.string(),
  }),
  fotos: z.array(
    z.object({
      pedido_id: z.number(),
      codLocal: z.string(),
      observaciones: z.string().nullable(),
      fecha: z.string(),
      v: z.coerce.number(),
      descuento_pc: z.string(),
      comision_pc: z.coerce.number(),
      dia: z.string(),
      cantidad: z.coerce.number(),
    })
  ),
  detalle_fotos: z.array(
    z.object({
      descripcion: z.string(),
      cantidad: z.coerce.number(),
    })
  ),
  totales_por_color: z.object({
    Blanco: z.coerce.number().default(0),
    Negro: z.coerce.number().default(0),
  }),
  nfotos: z.number(),
  pc: z.number(),
  total_premios: z.number(),
  pagos: z.array(
    z.object({
      id: z.number(),
      fecha: z.string(),
      valor: z.coerce.number(),
      moneda: z.string(),
      valor_n: z.string(),
      obs: z.string(),
      cuenta_id: z.number(),
    })
  ),
  pagos_por_colores: z.record(z.string(), z.coerce.number()),
  total_pagos: z.coerce.number(),
  comisionesT: z.number(),
});

export const AdminConfigZod = z.object({
  temporadas: z.record(
    z.string(),
    z.object({
      id: z.number(),
      descripcion: z.string(),
      sitio_id: z.number(),
      ts: z.string(),
      prioridad: z.number(),
      activo: z.boolean(),
      desde: z.string(),
      hasta: z.string(),
      valor_ref: z.coerce.number(),
      min_fecha: z.string(),
      max_fecha: z.string(),
      lugares: z.array(
        z.object({
          lugar_id: z.number(),
          shape: z.string(),
          coords: z.string(),
        })
      ),
      fotografos: z.array(
        z.object({ fotografo_id: z.number(), numero: z.number() })
      ),
      precios: z.record(
        z.string(),
        z.record(
          z.string(),
          z.object({
            activo: z.boolean(),
            precio: z.string(),
            pref: z.boolean(),
          })
        )
      ),
    })
  ),
  lugares: z.record(z.string(), z.string()),
  fotografos: z.record(
    z.string(),
    z.object({
      id: z.number(),
      nombre: z.string(),
      apellido: z.string(),
      email: z.string(),
      codcorto: z.string(),
      permisos: z.array(z.string()),
    })
  ),
  productos: z.array(
    z.object({
      id: z.number(),
      descripcion: z.string(),
      sitio_id: z.number(),
      ts: z.string(),
      automatico: z.boolean(),
    })
  ),
  locales: z.array(
    z.object({
      id: z.number(),
      codigo: z.string(),
      ts: z.string(),
      sitio_id: z.number(),
    })
  ),
});

export const AdminFotografoZod = z.object({
  id: z.number(),
  nombre: z.string(),
  apellido: z.string(),
  email: z.string(),
  codcorto: z.string().optional(),
  permisos: z.array(z.string()),
  password: z.string().optional(),
  foto: z.string().optional(),
});

export const UbicacionZod = z.object({
  id: z.number(),
  fotografo_id: z.number(),
  numero: z.number(),
  lugar_id: z.number().nullable(),
});
